/**
 * P17A-06 · The browser's side of P14-06.
 *
 * `~/server/security/cookies` is the one place that decides how this app's
 * cookies are written — and it is `server-only`, because it reads `~/env`. So
 * every cookie the BROWSER writes was outside it. That was one call site
 * (`analytics/consent.ts`) until this ticket moved the anonymous-visitor id off
 * the edge and made it two, which is exactly the shape P14-06's header
 * describes: attributes spelled out at each site, remembered at all but one,
 * and invisible until someone greps for it.
 *
 * Three helpers, so there is one string:
 *
 *   readClientCookie / writeClientCookie / deleteClientCookie
 *
 * `cookies.test.ts` grep-checks that nothing else hand-writes `SameSite=` or
 * `HttpOnly` into a cookie string, on the client as well as the server.
 *
 * ## `HttpOnly` is not offered, and that is the point
 *
 * A cookie written by `document.cookie` cannot be `HttpOnly` — the browser
 * refuses. Anything that needs it must be written by the server through
 * `serializeCookie()`. That is a real constraint on what may be written here:
 * the two current callers are an opaque analytics id and a consent token,
 * neither of which is a capability.
 *
 * ## `Secure` keys on `isSecureContext`
 *
 * Not on `location.protocol === "https:"`, which is what `consent.ts` did.
 * The browser accepts a `Secure` cookie exactly when the origin is
 * "potentially trustworthy" — which is `https:` **and** `localhost` /
 * `*.localhost` / `127.0.0.1` — and `isSecureContext` is that same predicate.
 * Keying on the protocol therefore dropped `Secure` on local development and
 * the e2e harness, the two places most likely to be inspected, while
 * `isSecureContext` gives them the attribute production gets and still omits
 * it on a plain-HTTP preview host where a `Secure` cookie would be silently
 * discarded.
 *
 * This is also the one deliberate difference from the server helper, which
 * gates `Secure` on `NODE_ENV === "production"` — it has to, because
 * production terminates TLS at the proxy and the origin sees `http:`. The
 * browser has no such problem: it knows whether its own connection is secure.
 */

/** The value of one cookie, decoded, or null when absent or empty. */
export function readClientCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() === name) {
      return decodeURIComponent(part.slice(eq + 1).trim()) || null;
    }
  }
  return null;
}

export interface ClientCookieOptions {
  /** Seconds. Omit for a session cookie. */
  maxAge?: number;
  /**
   * Default "lax", matching `cookieOptions()`: sent on top-level navigations,
   * withheld on cross-site subrequests.
   */
  sameSite?: "lax" | "strict";
  path?: string;
}

/**
 * Write a cookie from the browser. No-op on the server.
 *
 * The value is URI-encoded here, so callers pass the raw value — the mirror of
 * `serializeCookie()`, which deliberately does NOT re-encode because its
 * callers hand it signed tokens they have already encoded themselves.
 */
export function writeClientCookie(
  name: string,
  value: string,
  options: ClientCookieOptions = {},
): void {
  if (typeof document === "undefined") return;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Path=${options.path ?? "/"}`,
    `SameSite=${options.sameSite === "strict" ? "Strict" : "Lax"}`,
  ];
  if (options.maxAge !== undefined) parts.splice(2, 0, `Max-Age=${options.maxAge}`);
  if (typeof window !== "undefined" && window.isSecureContext) parts.push("Secure");
  document.cookie = parts.join("; ");
}

/** Expire a cookie this browser wrote. */
export function deleteClientCookie(name: string, path = "/"): void {
  writeClientCookie(name, "", { maxAge: 0, path });
}
